import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MinimalNavBar } from "@/components/layout/MinimalNavBar";

const GUIDE_CONTENT = `
# Jobs Bot — User Guide

## 1. Register / Sign In

**Option A — Email + password**
1. Go to [Register](/register), enter email + username + password
2. Check inbox → click verification link
3. Redirected to Dashboard

**Option B — Magic link** *(no password)*
1. On [Sign in](/login), enter email → click "Send magic link"
2. Check inbox → click link → logged in instantly
3. If email not registered, account is created automatically

**Option C — Sign in with Google**
1. On [Sign in](/login) or [Register](/register), click **Sign in with Google**
2. Complete Google's consent screen
3. Redirected to Dashboard — account created automatically on first use, linked on subsequent logins

---

## 2. Fill in Your Profile *(do this first — everything depends on it)*

Go to **Profile** (nav bar, top right).

Four tabs:

**Experience Vault** — your complete professional history in Markdown: work experience, education, skills, projects. The AI reads this to tailor every document. Be thorough.

**Preferences** — target roles, salary range, remote/location constraints, culture preferences. Markdown.

**CV Template** — your base CV structure in Markdown. AI uses this as scaffold when generating tailored CVs. Comes prepopulated with a default template — editing is optional.

**Cover Letter Template** — same, for cover letters. Also prepopulated; editing is optional.

Hit **Save** in the action bar (between tabs and content) after editing any tab.

---

## 3. Add a Job

Click **Add Job** in the nav bar.

**Option A — URL** *(preferred)*: paste the job posting URL → system scrapes company name, title, description, deadline, contact info automatically.

**Option B — Manual**: if scraping fails (bot-protected page, SPA), a form appears — paste the job description and fill in fields by hand.

Both paths produce an identical job record.

---

## 4. Review the Job Page

Job page has four tabs:

| Tab | What it shows |
|-----|--------------|
| **Job Description** | Scraped/entered JD, rendered Markdown |
| **Match Analysis** | AI alignment score, success probability, strengths & gaps |
| **Resume** | Tailored CV editor |
| **Cover Letter** | Tailored cover letter editor |

Match Analysis runs automatically when the job is created (if Experience Vault is non-empty). Click **Re-analyse** to regenerate after updating your profile.

---

## 5. Generate Tailored Documents

In the **Resume** or **Cover Letter** tab:

1. Click **Generate** — AI produces a tailored draft using your Experience Vault + template + JD. Streams in real-time.
2. Read it. Edit directly in the Markdown editor if needed.
3. Click **Save** to snapshot the version.

---

## 6. Refine with AI

Still in the Resume/Cover Letter tab:

1. Click **Refine** → modal opens → describe what to change (e.g. "make the summary more concise", "add more emphasis on Python experience")
2. Optionally **select text first** — it pre-fills the modal with that selection as context
3. Click **Apply** → AI updates the document; editor is read-only during streaming

Repeat as many times as needed. Each saved version is kept in history.

---

## 7. Version History

Click **History** → see all saved versions (v1, v2…) with timestamps.

- **Preview** any version → full-screen rendered view
- **Restore** any version → loads it back into the editor
- Latest saved version shows **Current** label if editor matches it

---

## 8. Preview & Export PDF

Click **Preview** from the editor:

- Draft saved → **Download PDF** button
- Unsaved changes → **Save and Download PDF** button (saves first, then downloads)

PDF preserves all Markdown formatting.

---

## 9. Track Applications on Dashboard

[Dashboard](/dashboard) table shows all jobs: Company, Title, Scores, Dates, Status.

- **Status dropdown** on each job page: update to \`Applied\`, \`Interviewing\`, \`Offer\`, \`Rejected\`, etc.
- **Filters**: by status, company, job title keyword
- **Sort**: click any column header
- Filters and sort order persist between sessions

---

## Recommended first-session flow

[Register](/register) → fill Profile (all 4 tabs) → Add first job → check Match Analysis → Generate CV → Refine → Download PDF
`;

export default function HelpPage() {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100">
      <MinimalNavBar />
      <main className="max-w-3xl mx-auto px-6 py-10 prose prose-slate dark:prose-invert">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children }) =>
              href?.startsWith("/") ? (
                <Link to={href}>{children}</Link>
              ) : (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
          }}
        >
          {GUIDE_CONTENT}
        </ReactMarkdown>
      </main>
    </div>
  );
}
